import React, { useState } from "react";

import styled from "styled-components";
import { Table } from "antd";

import "antd/dist/antd.min.css";

import { theme } from "../../../theme";
import { numberWithCommas } from "../../../shared/utils";
import PaginationComponent from "../Pagination";
import Buttons from "../../atoms/Buttons";
import PageSelect from "./components/PageSelect";
import QnA from "./components/QnA";
import EmptyBoxComp from "../EmptyBoxComp";
import { ReactComponent as Minus } from "../../../assets/icons/Minus.svg";
import { ReactComponent as Plus } from "../../../assets/icons/ListPlus.svg";

interface TableProps {
  title: string;
  columns: any[];
  data: any;
  totalCount: number;
  pageSize: number;
  onPageChanged?: null | ((page: number) => void);
  onPageSizeChanged?: null | ((pageSize: number) => void);
  onAdditional?: React.ReactNode;
  buttonText?: string;
  buttonFunc?: any;
  deleteButton?: React.ReactNode;
  handleModal: any;
  currentPage: number;
  rowSelection?: any;
  isLoading: boolean;
  isGenerateBanned?: boolean;
}
const ExpandableTable: React.FC<TableProps> = ({
  title,
  columns,
  data,
  totalCount,
  onPageChanged,
  onPageSizeChanged,
  pageSize,
  handleModal,
  currentPage,
  onAdditional,
  rowSelection,
  deleteButton,
  buttonFunc,
  buttonText,
  isLoading,

  isGenerateBanned,
}) => {
  const [expandedKey, setExpandedKey] = useState<number[]>([]);

  return (
    <Wrapper>
      <TableHeader>
        <TextPlace>{title}</TextPlace>
        <ButtonWrap>
          {onPageSizeChanged && (
            <div
              style={{
                marginRight: "24px",
                width: "135px",
                display: "flex",
                flexWrap: "nowrap",
                lineHeight: "32px",
              }}
            >
              <PageSelect
                value={pageSize}
                onPageSizeChanged={onPageSizeChanged}
              />
              &nbsp; 개씩 보기
            </div>
          )}
          {onAdditional && (
            <AdditionalButtonWrapper>{onAdditional}</AdditionalButtonWrapper>
          )}
          {!isGenerateBanned && (
            <Buttons
              disabled={isGenerateBanned}
              type="main"
              text={buttonText || "등록하기"}
              onClick={buttonFunc || (() => handleModal("generate"))}
            />
          )}
        </ButtonWrap>
      </TableHeader>
      <TableBodyWrapper>
        {isLoading ? (
          <TableContent loading />
        ) : data?.length > 0 ? (
          <TableContent
            showSorterTooltip={false}
            rowSelection={rowSelection}
            columns={[
              ...columns,
              {
                onHeaderCell: () => ({
                  className: "expand-icon",
                }),
                dataIndex: "noData",
                width: 10,
                render: (col, record: any) => (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    {expandedKey.includes(record.id) ? <Minus /> : <Plus />}
                  </div>
                ),
              },
            ]}
            dataSource={data}
            pagination={onPageChanged === null ? undefined : false}
            loading={false}
            expandable={{
              expandedRowRender: (record: { [index: string]: any }) => (
                <QnA
                  question={record.body}
                  questionMedias={record.inquiryMedias?.map(
                    (val: any) => val?.media
                  )}
                  answer={record.reply || ""}
                />
              ),
              expandRowByClick: true,
              showExpandColumn: false,

              onExpand: (isExpanded, record: any) => {
                if (isExpanded) setExpandedKey([...expandedKey, record.id]);
                else {
                  const newKeys = expandedKey.filter(
                    (value: number) => value !== record.id
                  );

                  setExpandedKey(newKeys);
                }
              },
            }}
            sticky
            rowKey="id"
            size="large"
          />
        ) : (
          <EmptyBoxComp />
        )}
      </TableBodyWrapper>
      {data?.length > 0 && (
        <FooterWrapper>
          <TableFooter>
            <DeleteWrapper>
              {deleteButton}
              <span>
                총 <TotalCount>{numberWithCommas(totalCount)}</TotalCount>
              </span>
            </DeleteWrapper>
            {onPageChanged && (
              <PaginationComponent
                size="default"
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                setCurrentPage={(page) => onPageChanged(page)}
              />
            )}
          </TableFooter>
        </FooterWrapper>
      )}
    </Wrapper>
  );
};
const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 24px;
  box-shadow: inset 0px -1px 0px #e2e2e2;
  background-color: white;
  max-height: 65px;
  height: 65px;
`;
const FooterWrapper = styled.div`
  padding: 0px 24px 24px 24px;
  background-color: white;
  border-radius: 0px 0px 16px 16px;
`;
const TableFooter = styled(TableHeader)`
  min-height: 64px;
  padding: 20px 24px;
  box-shadow: inset 0px -1px 0px transparent;
`;
const TextPlace = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #242426;
`;
const ButtonWrap = styled.div`
  display: flex;

  justify-content: flex-end;
`;
const TableBodyWrapper = styled.div`
  padding: 24px;
  background-color: white;
  padding-bottom: 0px;
`;
const TableContent = styled(Table)<{ padding?: string }>`
  width: 100%;
  background-color: white;

  cursor: default;
  overflow-y: hidden;
  border-color: ${theme.colors.gray6};

  .ant-table-cell .expand-icon {
    border: 0px solid transparent;
  }
  .ant-table-tbody > tr > td {
    ${theme.typo.heading.b14};
  }
  .ant-table-thead > tr {
    height: 48px;
  }
  .ant-table-thead > tr > th {
    padding-left: 12px;
  }
  .ant-table-tbody > tr:hover {
    background-color: white;
  }
  .ant-table-thead > tr > th {
    ${theme.typo.heading.b14};
  }
  .ant-table-row {
    height: 56px;
    cursor: pointer;
  }
  .ant-table-tbody > tr.ant-table-row > tr {
    height: 56px;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #f8f8f8;
  }
`;
const Wrapper = styled.div`
  margin: 40px 0px 0px 0px;
  border-radius: 16px;
  overflow-x: hidden;
  display: flex;

  flex-direction: column;
  flex: 1 1 fit-content;
`;

const AdditionalButtonWrapper = styled.div`
  margin-right: 24px;
  margin-left: 24px;
`;
const TotalCount = styled.span`
  color: ${theme.colors.gray1};
  ${theme.typo.heading.b14};
`;
const DeleteWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

export default ExpandableTable;
